import type { MateInRoom } from './types'
import type { RoomStorage } from './infrastructure.types'
import type { GameSetup, SetupReadiness } from './gameSetup.types'
import type { GameOptionDefinition, GameSavedOptions } from '@merels/game-logic/types/options'
import type { BroadcastListener } from './broadcast.types'
import type { HostPlays } from '../room/hosting/hosting'
import type {
  UpdateMatesArgs,
  StartGameArgs,
  MakeMoveArgs,
  ForceGameStateArgs,
  OpenGameArgs,
  ChoosePlayerCountArgs,
  UpdateMateAssignmentArgs,
  GetSavedGameOptionsArgs,
  SetSavedGameOptionsArgs,
  RestartGameArgs,
  GetThumbnailHistoryArgs,
  DeleteThumbnailFromHistoryArgs,
  GetGameChannelPreferenceArgs,
  SetGameChannelPreferenceArgs,
  SetActiveGameVersionArgs,
  SetGamelessModeArgs,
  GetChallengeConfigurationPreferenceArgs,
  SetChallengeConfigurationPreferenceArgs,
  EndGameArgs,
  StartGameFromSetupArgs,
  CancelGameSetupArgs,
  SetHostArgs,
  SetManagedModeArgs,
  SetHostTermArgs,
  SetCuratedModeArgs,
  SetCuratedGamesArgs,
} from '../schemas/action.schemas'

/** Who establishes the challenge that mates will face. */
export type ChallengeSource = 'opponent' | 'game'
/** How mates participate when the game establishes the challenge. */
export type GameChallengeArrangement = 'cooperative' | 'sameChallengeRace' | 'differentChallengeRace'
/** The saved and active configuration for a game's challenge source. */
export type ChallengeConfiguration =
  | { challengeSource: 'opponent' }
  | { challengeSource: 'game'; gameChallengeArrangement: GameChallengeArrangement }

/**
 * A room-ordered event used to reconstruct a game in clients.
 *
 * `forceGameState` is interpreted by platform replay code, never delivered
 * to game logic as a move.
 */
export type GameAction =
  | { type: 'move'; move: { type: string; args?: unknown[] }; mateId: string; stateHash?: string; asHost?: boolean }
  | { type: 'forceGameState'; state: { G?: unknown; ctx?: unknown } }

/** Metadata published with a game version and required for room orchestration. */
export interface GameCatalogMetadata {
  displayName?: string
  description?: string
  minPlayers: number
  maxPlayers: number
  supportsSharedScreen?: boolean
  /** Option definitions for this game (if any). */
  options?: GameOptionDefinition[]
  /**
   * Returns the player IDs for a given number of players.
   * e.g., tictactoe returns ['X', 'O'], battleship returns ['p1', 'p2']
   * If not provided, defaults to ['0', '1', ...] based on numPlayers.
   */
  playerIds?: string[] | Record<number, string[]>
  /** Challenge sources that this game implements. Defaults to opponent. */
  challengeSources?: ChallengeSource[]
  /** Participation arrangements implemented when the game establishes the challenge. */
  gameChallengeArrangements?: GameChallengeArrangement[]
}

/**
 * Room container with dependencies.
 *
 * This is the environment required by the room runtime; both the web app (local mode)
 * and the worker provide adapters for these dependencies.
 */
/** A game's published client build, resolved once and pinned for a game's lifetime. */
export interface ResolvedGameVersion {
  versionId: string
  clientBundleUrl: string
  styleUrl?: string
  metadata: GameCatalogMetadata
}

/**
 * Resolves which published version of a game's client a room should pin to
 * at game start, and (for non-"stable" channels) keeps the room subscribed
 * to further publishes so it can push live updates to all its clients.
 */
export interface GameCatalogClient {
  /** Resolve the published version and its room metadata for a game channel. */
  resolveVersion(gameId: string, channel: string): Promise<ResolvedGameVersion | null>
  /**
   * Register this room to receive a push (via the caller's own mechanism —
   * e.g. Room.updateGameVersion) whenever a new version publishes to this
   * game+channel. Only meaningful for non-"stable" (live-updating) channels;
   * optional since not every platform wires push notifications.
   *
   * Takes no room identifier: each GameCatalogClient instance is
   * constructed per-room by the caller (RoomDO, local room, ...), so the
   * implementation already knows which room it represents via its own
   * closure — it doesn't need packages/room to thread one through.
   */
  subscribe?(gameId: string, channel: string): Promise<void>
  /** Undo a prior subscribe(), e.g. when the game ends. */
  unsubscribe?(gameId: string, channel: string): Promise<void>
}

/**
 * Who is calling a room action, as established by the adapter: the worker
 * from the request's client header and authenticated user, a local room from
 * its own client. Omitted for trusted internal calls.
 */
export interface RoomCaller {
  clientId?: string
  isOwner: boolean
}

/**
 * The player ID under which every client replays a host action. Games
 * receive it as `playerID` in host-action moves; the public SDK names it as
 * the `HostPlayerID` type.
 */
export const HOST_PLAYER_ID = 'host'

/** Request header a networked client uses to identify itself to its room. */
export const ROOM_CLIENT_ID_HEADER = 'X-Merels-Client-Id'

export interface RoomContainer {
  /** Persistence for the room state (mates + activeGame). */
  storage: RoomStorage
  /** Broadcast mechanism to notify connected clients about state changes. */
  broadcastChannel: import('./infrastructure.types').BroadcastChannel
  /** Catalog used to pin a published game version before the room starts. */
  gameCatalog: GameCatalogClient
}

export interface PublicActiveGame {
  gameId: string
  startedAt: string
  gameOptions?: Record<string, unknown>
  /** Platform-selected challenge configuration, kept separate from game options. */
  challengeConfiguration?: ChallengeConfiguration
  /** Whether the room was managed when this game started; games may assume a host is present. */
  managed?: boolean
  mateIDs?: string[]
  mateSlotMap?: Record<string, number>
  observerIDs?: string[]
  mateGroups?: string[][]
  /** Game-specific player IDs, e.g. ['X', 'O'] for tictactoe. Index = bgio player slot. */
  playerIds?: string[]
  /**
   * The channel this game instance is tracking (default "stable"). On
   * "stable", gameVersion is resolved once at start and never changes. On
   * any other channel (e.g. "preview"), the room stays subscribed and
   * pushes a new gameVersion — via the same room broadcast clients already
   * listen to — whenever a new version publishes to it.
   */
  channel?: string
  /**
   * The published client version this game instance currently uses. On the
   * "stable" channel this is set once at start and is immutable for the
   * rest of the game. On other channels it can update live (see `channel`).
   * Undefined for statically-bundled games.
   */
  gameVersion: ResolvedGameVersion
  /**
   * Seed for this game instance's PRNG (bgio's `random` plugin), minted once
   * by the room at startGame. Clients must use this same seed when calling
   * InitializeGame so setup()/moves stay deterministic across clients.
   */
  seed: string
  /** The action log for client-side state reconstruction (e.g. for Tic-Tac-Toe). */
  actions: GameAction[]
}

/**
 * Public room state returned by `Room.getRoomState()`.
 *
 * This is the public room state shape returned to callers.
 * Reconstructed game state is never part of this room wire contract.
 */
export interface PublicRoomState {
  activeGame: PublicActiveGame | null
  gameSetup: GameSetup | null
  mates: MateInRoom[]
  createdAt: string
  roomName?: string
  ownerName?: string
  imageUrl?: string
  ownerId?: string
  videoEnabled?: boolean
  /** Whether the room is currently in "gameless" mode (game hidden, video-only). Defaults to false. */
  gameless?: boolean
  /** Mates who coordinate the room. */
  hostMateIds: string[]
  /** Whether the room is in managed mode: only hosts start or end games. */
  managed: boolean
  /** What the room calls a host, such as "Teacher". */
  hostTerm: string
  /** Each game's Host plays setting where the owner saved one; other games use the default. */
  hostPlaysByGameId: Record<string, HostPlays>
  /** Whether participants see only the curated games. */
  curated: boolean
  /** The owner's ordered selection of games for a curated room. */
  curatedGameIds: string[]
}

/**
 * Room interface - the public API.
 *
 * This is the shared runtime API used by:
 * - the worker (networked rooms)
 * - the web app (local rooms)
 *
 * Important vocabulary:
 * - **mateId** identifies a person/bot in the room (stable identity).
 * - **player slot** / "player" is a logical seat inside a specific game instance.
 *
 * All action methods take their arguments as a single object.
 */
export interface Room {
  /**
   * Start a new game in this room.
   *
   * @param args.gameId - identifier of the game plugin to start.
   *
   * If seating-related fields are omitted, the implementation will derive the initial `mateIDs`
   * from `roomState.mates`.
   */
  startGame(args: StartGameArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string }>

  /**
   * End the currently active game, if any.
   *
   * If there is no active game, returns `{ success: false }`.
   */
  endGame(args?: EndGameArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string }>

  /**
   * Called by the caller's own push-notification plumbing (see
   * GameCatalogClient.subscribe) when a new version publishes to a channel
   * this room's active game is tracking. No-ops if there's no active game,
   * the gameId doesn't match, or the channel doesn't match — so a stray or
   * late notification after the game ended/changed is harmless.
   */
  updateGameVersion(args: { gameId: string; channel: string; version: ResolvedGameVersion }): Promise<{ success: boolean }>

  /**
   * Debug/admin override: force the active game to render at an explicit
   * published version, regardless of the channel it's tracking — used to
   * preview an arbitrary historical build. Unlike updateGameVersion this
   * doesn't check or change `channel`, so a later live channel push can
   * still overwrite it. No-ops if there's no active game or the gameId
   * doesn't match.
   */
  setActiveGameVersion(args: SetActiveGameVersionArgs): Promise<{ success: boolean }>

  /**
   * Read the canonical room state from storage.
   *
   * This is primarily used by:
   * - local mode to initialize appState after creating a local room
   * - tests and debug tooling
   */
  getRoomState(): Promise<PublicRoomState>

  /**
   * Update mates for a specific client.
   *
   * Replaces all mates with the given clientId with the provided list.
   * - Mates from other clients are preserved.
   * - For mates that already exist (by mateId), joinedAt is preserved.
   * - For new mates, joinedAt is assigned by the runtime.
   */
  updateMates(args: UpdateMatesArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string; mates?: MateInRoom[] }>

  /**
   * Debug/admin operation: remove all registered mates (and clear any active game).
   * Intended for development/debug flows only.
   */
  flushMates(): Promise<{ success: boolean }>

  /**
   * Get the thumbnail history remembered for a mate's `persistentId` in this room,
   * most-recent-first. Lets a mate switch back to a previously uploaded photo.
   */
  getThumbnailHistory(args: GetThumbnailHistoryArgs): Promise<{ history: string[] }>

  /**
   * Remove one photo from a mate's thumbnail history in this room.
   */
  deleteThumbnailFromHistory(args: DeleteThumbnailFromHistoryArgs): Promise<{ history: string[] }>

  /**
   * Apply a move in the active game.
   *
   * @param args.move - game-specific payload. The current game protocol uses
   *   `{ type: string, args?: any[] }`.
   * @param args.mateId - optional at the type level because some future/alternate game
   *   runtimes may not require a sender identity.
   *
   * `mateId` is required and the method will return
   * an error if it is missing.
   */
  makeMove(args: MakeMoveArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string; errorCode?: string }>

  /**
   * Append a platform-owned state checkpoint for debugging or games that
   * deliberately use platform-controlled state transitions. The room does
   * not inspect the state or run game code.
   */
  forceGameState(args: ForceGameStateArgs): Promise<{ success: boolean; error?: string }>

  /**
  // ─────────────────────────────────────────────────────────────────────────────
  // Game Setup Methods
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Open a game. The room decides whether to start immediately or enter setup.
   *
   * Possible outcomes:
   * - 'gameStarted': No setup needed, game started immediately
   * - 'setupStarted': Setup phase initiated (choosePlayerCount or assignSlots)
   *
   * Returns error if:
   * - A game is already active
   * - A setup is already in progress
   * - Game type is unknown
   * - Not enough mates for this game
   */
  openGame(args: OpenGameArgs, caller?: RoomCaller): Promise<{
    success: boolean
    error?: string
    outcome?: 'gameStarted' | 'setupStarted'
    setup?: GameSetup
    readiness?: SetupReadiness
    gameState?: unknown
  }>

  /**
   * Choose the number of player slots.
   *
   * Any client can make this choice on behalf of everyone.
   * Only valid when setup.phase === 'choosePlayerCount'.
   *
   * After choosing:
   * - If mateCount === slotCount: Game starts immediately
   * - Otherwise: Moves to 'assignSlots' phase
   *
   * Broadcasts 'setupChanged' or 'gameStarted'.
   */
  choosePlayerCount(args: ChoosePlayerCountArgs, caller?: RoomCaller): Promise<{
    success: boolean
    error?: string
    outcome?: 'gameStarted' | 'movedToAssignSlots'
    setup?: GameSetup
    readiness?: SetupReadiness
    gameState?: unknown
  }>

  /**
   * Update a mate's slot assignment during setup.
   *
   * Each client can only update their own mates.
   * Only valid when setup.phase === 'assignSlots'.
   *
   * Set slotIndex to null to unassign (mate becomes observer).
   * Broadcasts 'setupChanged'.
   */
  updateMateAssignment(args: UpdateMateAssignmentArgs): Promise<{
    success: boolean
    error?: string
    setup?: GameSetup
    readiness?: SetupReadiness
  }>

  /**
   * Start the game from setup (callable when readiness.canStart is true).
   *
   * Only valid when setup.phase === 'assignSlots'.
   * Validates all slots are filled, then starts the game.
   * Broadcasts 'gameStarted'.
   */
  startGameFromSetup(args?: StartGameFromSetupArgs, caller?: RoomCaller): Promise<{
    success: boolean
    error?: string
    gameState?: unknown
  }>

  /**
   * Cancel setup without starting a game.
   *
   * Broadcasts 'setupCancelled'.
   */
  cancelGameSetup(args?: CancelGameSetupArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string }>

  /**
   * Get current setup state (for initial sync when client connects).
   */
  getGameSetup(): Promise<{ setup: GameSetup; readiness: SetupReadiness } | null>

  // ─────────────────────────────────────────────────────────────────────────────
  // Game Options Methods
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Get saved game options for a specific game.
   *
   * Returns the saved options structure, or an empty structure if none saved.
   */
  getSavedGameOptions(args: GetSavedGameOptionsArgs): Promise<GameSavedOptions>

  /**
   * Save game options for a specific game.
   *
   * @param args.gameId - The game to save options for
   * @param args.scope - 'global' or 'perPlayerCount'
   * @param args.values - Option values to save (merged with existing)
   * @param args.playerCount - Required when scope is 'perPlayerCount'
   *
   * Validates options against game definitions and broadcasts 'optionsChanged'.
   */
  setSavedGameOptions(args: SetSavedGameOptionsArgs): Promise<{
    success: boolean
    error?: string
    savedOptions?: GameSavedOptions
  }>

  /**
   * Restart the active game with updated options.
   *
   * Ends the current game and starts a new one with the same participants
   * but optionally with different options.
   *
   * If scope/values are provided, saves those options first.
   */
  restartGame(args: RestartGameArgs, caller?: RoomCaller): Promise<{
    success: boolean
    error?: string
    gameState?: unknown
  }>

  /**
   * Get the sticky release channel preference for a game (e.g. "preview"),
   * or `channel: null` if none is set — meaning startGame falls back to its
   * hardcoded default ("stable").
   */
  getGameChannelPreference(args: GetGameChannelPreferenceArgs): Promise<{ channel: string | null }>

  /**
   * Set the sticky release channel preference for a game.
   *
   * This is the fallback channel used by startGame/openGame/restartGame
   * whenever they're not given an explicit channel — so it's what keeps a
   * room "following preview" across rematches/restarts instead of quietly
   * reverting to "stable" every time a new game starts. Broadcasts
   * 'gameChannelPreferenceChanged'.
   */
  setGameChannelPreference(args: SetGameChannelPreferenceArgs): Promise<{ success: boolean }>

  // ─────────────────────────────────────────────────────────────────────────────
  // Gameless Mode Methods
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Set the room's "gameless" flag: any mate can call this and it applies to
   * every connected client. It only affects layout — it does not touch the
   * active game's state, so turning it off returns to the game as it was.
   * Broadcasts 'gamelessModeChanged'.
   */
  setGamelessMode(args: SetGamelessModeArgs): Promise<{ success: boolean }>
  getChallengeConfigurationPreference(args: GetChallengeConfigurationPreferenceArgs): Promise<{ challengeConfiguration: ChallengeConfiguration | null }>
  setChallengeConfigurationPreference(args: SetChallengeConfigurationPreferenceArgs): Promise<{ success: boolean }>

  /** Owner only: designate or remove a host. */
  setHost(args: SetHostArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string }>

  /**
   * Owner only: switch managed mode, in which only hosts start or end games.
   * The mode belongs to the room and does not depend on who is present.
   */
  setManagedMode(args: SetManagedModeArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string }>

  /** Owner only: name the host role, such as "Teacher", wherever the room shows it. */
  setHostTerm(args: SetHostTermArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string }>

  /** Owner only: switch curation, in which participants see only the curated games. */
  setCuratedMode(args: SetCuratedModeArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string }>

  /** Owner only: replace the ordered selection of curated games. */
  setCuratedGames(args: SetCuratedGamesArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string }>

  // ─────────────────────────────────────────────────────────────────────────────
  // Subscription Methods
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Subscribe to room messages (broadcasts).
   *
   * Returns an unsubscribe function.
   */
  onMessage(listener: BroadcastListener): () => void
}
